import { useCallback, useMemo } from 'react';

import { useSyncedPref } from '#hooks/useSyncedPref';

import type { PlanningGoal } from './types';
import { parsePlanningGoals, serializePlanningGoals } from './utils';

type SetPlanningGoalsAction =
  | PlanningGoal[]
  | ((previousGoals: PlanningGoal[]) => PlanningGoal[]);

export function usePlanningGoals() {
  const [serializedGoals, setSerializedGoals] = useSyncedPref('planning-goals');

  const goals = useMemo(
    () => parsePlanningGoals(serializedGoals),
    [serializedGoals],
  );

  const setGoals = useCallback(
    (nextGoals: SetPlanningGoalsAction) => {
      const resolvedGoals =
        typeof nextGoals === 'function' ? nextGoals(goals) : nextGoals;
      setSerializedGoals(serializePlanningGoals(resolvedGoals));
    },
    [goals, setSerializedGoals],
  );

  const upsertGoal = useCallback(
    (goal: PlanningGoal) => {
      setGoals(previousGoals => {
        const existingGoalIndex = previousGoals.findIndex(
          currentGoal => currentGoal.id === goal.id,
        );

        if (existingGoalIndex === -1) {
          return [...previousGoals, goal];
        }

        const nextGoals = [...previousGoals];
        nextGoals[existingGoalIndex] = goal;
        return nextGoals;
      });
    },
    [setGoals],
  );

  const deleteGoal = useCallback(
    (goalId: string) => {
      setGoals(previousGoals =>
        previousGoals.filter(goal => goal.id !== goalId),
      );
    },
    [setGoals],
  );

  return {
    goals,
    setGoals,
    upsertGoal,
    deleteGoal,
  };
}
